import { Alert, Center, Loader, Paper } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';
import { CashflowsLayout } from 'src/cashflows/redesign/CashflowsLayout';
import { ExpenditureFormModal } from 'src/cashflows/redesign/ExpenditureFormModal';
import { IncomeFormModal } from 'src/cashflows/redesign/IncomeFormModal';
import { LifeEventFormModal } from 'src/cashflows/redesign/LifeEventFormModal';
import { reportSaveFailure } from 'src/cashflows/redesign/reportSaveFailure';
import {
  useSaveExpenditure,
  useSaveIncome,
  useSaveLifeEvent,
} from 'src/cashflows/redesign/useCashflowSaves';
import { memberIndex, toGroupMembers } from 'src/cashflows/redesign/useCashflowMembers';
import {
  ExpenditureRow,
  IncomeRow,
  LifeEventRow,
  buildExpenditureRows,
  buildIncomeRows,
  buildLifeEventRows,
} from 'src/cashflows/rows';
import { Period } from 'src/cashflows/utils';
import { showError, showSuccess } from 'src/toasts/toasts';
import { useFamilyCashflows } from './useFamilyCashflows';
import { FamilyMember } from './useFamilyMembers';

export function FamilyCashflowsRedesign({
  members,
  modelsSection,
}: {
  members: FamilyMember[];
  modelsSection: ReactNode;
}) {
  const { incomes, expenditures, lifeEvents, isLoading, hasErrors } = useFamilyCashflows(members);
  const [period, setPeriod] = useState<Period>('monthly');
  const [incomeOpen, income] = useDisclosure(false);
  const [expenditureOpen, expenditure] = useDisclosure(false);
  const [lifeEventOpen, lifeEvent] = useDisclosure(false);
  const [editedIncome, setEditedIncome] = useState<IncomeRow | undefined>();
  const [editedExpenditure, setEditedExpenditure] = useState<ExpenditureRow | undefined>();
  const [editedLifeEvent, setEditedLifeEvent] = useState<LifeEventRow | undefined>();

  const saveIncome = useSaveIncome();
  const saveExpenditure = useSaveExpenditure();
  const saveLifeEvent = useSaveLifeEvent();

  const groupMembers = toGroupMembers(members);
  const byClientId = memberIndex(groupMembers);

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  const save = <T,>(
    persist: (submission: T) => Promise<void>,
    noun: string,
    close: () => void,
    clearEdited: () => void,
  ) => {
    return async (submission: T) => {
      try {
        await persist(submission);
        showSuccess(`${noun} saved successfully`);
        close();
        clearEdited();
      } catch (error) {
        if (reportSaveFailure(error, byClientId, noun)) {
          close();
          clearEdited();
          return;
        }
        showError(`Failed to save ${noun.toLowerCase()}`);
      }
    };
  };

  return (
    <>
      <CashflowsLayout
        period={period}
        onPeriodChange={setPeriod}
        nameMembers
        incomeRows={buildIncomeRows(incomes, byClientId)}
        expenditureRows={buildExpenditureRows(expenditures, byClientId)}
        lifeEventRows={buildLifeEventRows(lifeEvents, byClientId)}
        incomeActions={{
          onAdd: () => {
            setEditedIncome(undefined);
            income.open();
          },
          onRowClick: row => {
            setEditedIncome(row);
            income.open();
          },
        }}
        expenditureActions={{
          onAdd: () => {
            setEditedExpenditure(undefined);
            expenditure.open();
          },
          onRowClick: row => {
            setEditedExpenditure(row);
            expenditure.open();
          },
        }}
        lifeEventActions={{
          onAdd: () => {
            setEditedLifeEvent(undefined);
            lifeEvent.open();
          },
          onRowClick: row => {
            setEditedLifeEvent(row);
            lifeEvent.open();
          },
        }}
        banner={
          hasErrors ? (
            <Alert
              variant="light"
              color="yellow"
              icon={<IconAlertTriangle size={18} />}
              title="Some data could not be loaded"
            >
              Cashflow data for one or more family members failed to load, so the totals below may
              be incomplete.
            </Alert>
          ) : null
        }
        modelsSection={modelsSection}
      />

      <IncomeFormModal
        opened={incomeOpen}
        onClose={income.close}
        onSubmit={save(saveIncome, 'Income', income.close, () => setEditedIncome(undefined))}
        members={groupMembers}
        initialRow={editedIncome}
      />
      <ExpenditureFormModal
        opened={expenditureOpen}
        onClose={expenditure.close}
        onSubmit={save(saveExpenditure, 'Expenditure', expenditure.close, () =>
          setEditedExpenditure(undefined),
        )}
        members={groupMembers}
        initialRow={editedExpenditure}
      />
      <LifeEventFormModal
        opened={lifeEventOpen}
        onClose={lifeEvent.close}
        onSubmit={save(saveLifeEvent, 'Life event', lifeEvent.close, () =>
          setEditedLifeEvent(undefined),
        )}
        members={groupMembers}
        initialRow={editedLifeEvent}
      />
    </>
  );
}
